/**
 * Traingle — Predictive Search Autocomplete
 * Queries native Shopify /search/suggest.json endpoint with debounce and keyboard accessibility
 */

class PredictiveSearch {
  constructor() {
    this.searchModal = document.getElementById('SearchModal');
    this.searchInput = document.getElementById('PredictiveSearchInput');
    this.searchResultsContainer = document.getElementById('PredictiveSearchResults');
    this.searchTriggers = document.querySelectorAll('[data-action="open-search"]');
    this.closeTriggers = document.querySelectorAll('[data-action="close-search"]');
    this.debounceTimer = null;

    this.init();
  }

  init() {
    // Open Search Modal
    this.searchTriggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close Search Modal
    this.closeTriggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Input changes
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        clearTimeout(this.debounceTimer);
        const query = this.searchInput.value.trim();
        if (query.length < 2) {
          if (this.searchResultsContainer) {
            this.searchResultsContainer.innerHTML = '';
          }
          return;
        }

        this.debounceTimer = setTimeout(() => {
          this.fetchResults(query);
        }, 280);
      });
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  isOpen() {
    return this.searchModal && this.searchModal.classList.contains('is-open');
  }

  open() {
    if (!this.searchModal) return;
    this.searchModal.classList.add('is-open');
    const backdrop = document.getElementById('DrawerBackdrop');
    if (backdrop) backdrop.classList.add('is-open');
    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 100);
    }
  }

  close() {
    if (!this.searchModal) return;
    this.searchModal.classList.remove('is-open');
    const backdrop = document.getElementById('DrawerBackdrop');
    if (backdrop && !document.querySelector('.cart-drawer.is-open')) {
      backdrop.classList.remove('is-open');
    }
  }

  async fetchResults(query) {
    if (!this.searchResultsContainer) return;
    this.searchResultsContainer.innerHTML = '<div style="padding: 1.5rem; text-align: center; color: var(--color-slate-dark);">Searching atelier records...</div>';

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      this.renderResults(data.resources.results, query);
    } catch (err) {
      this.searchResultsContainer.innerHTML = '<div style="padding: 1rem; color: var(--color-error);">Unable to retrieve search results.</div>';
    }
  }

  renderResults(results, query) {
    const products = results.products || [];
    const collections = results.collections || [];

    if (products.length === 0 && collections.length === 0) {
      this.searchResultsContainer.innerHTML = `
        <div style="padding: 2rem; text-align: center;">
          <p style="font-family: var(--font-heading); font-size: 1.15rem; margin-bottom: 0.5rem;">No sartorial matches found</p>
          <p style="color: var(--color-slate-dark); font-size: 0.875rem;">Try searching for "Bespoke Suit", "Formal Shirt", or "Oxford Shoes".</p>
        </div>
      `;
      return;
    }

    let html = '';

    if (collections.length > 0) {
      html += `
        <div style="margin-bottom: 1.5rem;">
          <div style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--color-slate-dark); margin-bottom: 0.75rem;">Suggested Collections</div>
          <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
            ${collections.map(c => `
              <a href="${c.url}" class="badge" style="background: var(--color-cool-white); border: 1px solid var(--color-border); color: var(--color-primary-navy); padding: 0.4rem 0.8rem;">${c.title}</a>
            `).join('')}
          </div>
        </div>
      `;
    }

    if (products.length > 0) {
      html += `
        <div>
          <div style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--color-slate-dark); margin-bottom: 0.75rem;">Tailoring Results (${products.length})</div>
          <div style="display: flex; flex-direction: column; gap: 0.75rem;">
            ${products.map(p => `
              <a href="${p.url}" style="display: flex; align-items: center; gap: 1rem; padding: 0.6rem; border-radius: var(--radius-sm); border: 1px solid var(--color-border); background: var(--color-pure-white); transition: border-color 0.2s;">
                <img src="${p.image || ''}" alt="${p.title}" style="width: 52px; height: 68px; object-fit: cover; border-radius: var(--radius-sm); background: #eee;">
                <div>
                  <div style="font-family: var(--font-heading); font-weight: 600; font-size: 0.95rem; color: var(--color-primary-navy);">${p.title}</div>
                  <div style="font-size: 0.875rem; color: var(--color-electric-blue); font-weight: 600; margin-top: 0.2rem;">${p.price}</div>
                </div>
              </a>
            `).join('')}
          </div>
        </div>
        <div style="margin-top: 1.5rem; text-align: center;">
          <a href="/search?q=${encodeURIComponent(query)}" class="btn btn-navy btn-sm" style="width: 100%;">View All Search Results</a>
        </div>
      `;
    }

    this.searchResultsContainer.innerHTML = html;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.predictiveSearch = new PredictiveSearch();
});
